import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  computed,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import {
  PackageFulfillmentMode,
  PRODUCT_SELECTION_DIALOG_CONFIG,
  PRODUCT_SERVICE_CONFIG,
} from '../../internal.config';
import { formatCurrencyVND } from '@app/shared/utils/date-formatter.util';

export interface ProductItem {
  id: number;
  spuName: string;
  spuCode: string;
  skuName: string;
  skuCode: string;
  brandName: string;
  categoryName: string;
  salesPrice: number;
  costPrice: number;
  activityPrice?: number;
  clearingPrice?: number;
  displayPrice?: number;
  spuId: number;
  skuId: number;
  setFlag?: number;
  isPackage?: boolean;
  fulfillmentMode?: PackageFulfillmentMode;
  fulfillmentModeLabel?: string;
}

@Component({
  selector: 'app-product-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    DataTableComponent,
  ],
  templateUrl: './product-selection-dialog.component.html',
  styleUrls: ['./product-selection-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductSelectionDialogComponent {
  @Input() visible = false;
  @Input() products: ProductItem[] = [];
  @Input() loading = false;
  @Input() totalRecords = 0;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() selectionConfirm = new EventEmitter<ProductItem[]>();
  @Output() search = new EventEmitter<Record<string, string>>();
  @Output() searchReset = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();

  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  // Currency formatter method
  formatCurrency = formatCurrencyVND;

  // Dialog state
  selectedProducts = signal<ProductItem[]>([]);
  searchForm = signal<Record<string, string>>({
    spuName: '',
    spuCode: '',
    skuCode: '',
  });
  currentPage = signal(1);
  pageSize = signal(PRODUCT_SERVICE_CONFIG.defaultPageSize);

  // Configuration
  config = PRODUCT_SELECTION_DIALOG_CONFIG;
  serviceConfig = PRODUCT_SERVICE_CONFIG;

  dialogStyle = computed(() => ({
    width: this.config?.width,
    maxWidth: this.config?.maxWidth,
    height: this.config?.height,
    maxHeight: this.config?.maxHeight,
  }));

  // Computed properties
  hasSelection = computed(() => this.selectedProducts().length > 0);
  dialogTitle = computed(() =>
    this.translateService.instant(this.config.title),
  );

  onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
    if (!visible) {
      this.resetDialog();
    }
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.search.emit(this.searchForm());
  }

  onSearchReset(): void {
    const resetForm: Record<string, string> = {};
    this.config.searchFields.forEach((field) => {
      resetForm[field.key] = '';
    });
    this.searchForm.set(resetForm);
    this.currentPage.set(1);
    this.searchReset.emit();
  }

  onPageChange(event: any): void {
    this.currentPage.set(event.page + 1);
    this.pageChange.emit(event.page + 1);
  }

  onSelectionChange(selection: ProductItem[]): void {
    this.selectedProducts.set(selection);
  }

  /**
   * Check if a product is disabled (already selected)
   */
  isProductDisabled(product: ProductItem): boolean {
    return (product as any).disable === 1;
  }

  /**
   * Get the selection state for products (for data-table component)
   */
  getProductSelectionState(product: ProductItem): boolean {
    // If product is disabled (already selected), show it as selected but disabled
    if (this.isProductDisabled(product)) {
      return true;
    }

    // Otherwise, use normal selection logic
    return this.selectedProducts().some(
      (selected) => selected.skuCode === product.skuCode,
    );
  }

  onConfirm(): void {
    this.selectionConfirm.emit(this.selectedProducts());
    this.onVisibleChange(false);
  }

  onCancel(): void {
    this.onVisibleChange(false);
  }

  private resetDialog(): void {
    this.selectedProducts.set([]);
    this.currentPage.set(1);

    // Reset search form using configuration
    const resetForm: Record<string, string> = {};
    this.config.searchFields.forEach((field) => {
      resetForm[field.key] = '';
    });
    this.searchForm.set(resetForm);
  }
}
